/**
 * Shared helpers for DMS standalone flows (petty cash, cash deposit) — not voucher-specific.
 * Do not import voucher components or Send All logic here.
 */

/**
 * getAllRecords often returns either top-level `documents` or nested `storeWiseRecordsResponses[].documents`.
 * Use this so Store / L1+ lists are not empty when `documents` is [] but store-wise data exists.
 *
 * @param {object} rawResponse - apiData.data.data from getAllRecords
 * @returns {Array} parent or leaf rows from the API
 */
export function flattenDocumentsFromGetRecordsResponse(rawResponse) {
  const response = rawResponse || {};
  const docs = Array.isArray(response.documents) ? response.documents : [];
  if (docs.length > 0) {
    return docs;
  }
  const storeWise = Array.isArray(response.storeWiseRecordsResponses)
    ? response.storeWiseRecordsResponses
    : [];
  return storeWise.flatMap((s) => (Array.isArray(s?.documents) ? s.documents : []));
}

/**
 * Backend voucher parents use l1/l2/l3; storeColumns read L1/L2/L3 (same as useApi boolean processData path).
 */
export function normalizeVoucherParentRowForStoreColumns(row) {
  if (!row || typeof row !== "object") return row;
  const out = { ...row };
  if (row.l1 != null) out.L1 = row.L1 ?? row.l1;
  if (row.l2 != null) out.L2 = row.L2 ?? row.l2;
  if (row.l3 != null) out.L3 = row.L3 ?? row.l3;
  return out;
}

/**
 * Voucher list rows sometimes omit `vid`/`id` at the parent level; fall back to common API fields and attributes.
 * @param {object} row - table row.original from getRecords / getAllRecords
 * @returns {string|null}
 */
export function resolveVoucherListRowId(row) {
  if (!row || typeof row !== "object") return null;
  const candidates = [
    row.vid,
    row.id,
    row.parentId,
    row.parentDocumentId,
    row.dataModalRecordId,
    row.documentId,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== "") {
      return String(c).trim();
    }
  }
  const attrs = row.dataModalAttributeSet;
  if (!Array.isArray(attrs)) return null;
  const technicalNames = [
    "voucher_id",
    "voucherId",
    "parent_voucher_id",
    "parent_document_id",
    "document_id",
    "parent_id",
  ];
  for (const name of technicalNames) {
    const hit = attrs.find((a) => a?.metadataTechnicalName === name);
    const v = hit?.attributeValue;
    if (v != null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return null;
}

/** Flatten parent vouchers for Store lists when getAllRecords returns storeWiseRecordsResponses (not storeGetResponse). */
export function storeVoucherParentsFromGetRecordsResponse(rawResponse) {
  return flattenDocumentsFromGetRecordsResponse(rawResponse).map(
    normalizeVoucherParentRowForStoreColumns
  );
}

/**
 * Map a child (or leaf) record from getAllRecords to the flat row shape useApi uses for voucher-style children.
 * Keeps cash deposit table columns (sales_day, comment, total_cash_sales, cash_deposit, file_id, …) working.
 */
export function mapChildDocumentRow(doc) {
  const obj2 = {};
  doc?.dataModalAttributeSet?.forEach((data) => {
    obj2[data?.metadataTechnicalName] = data?.attributeValue;
    if (data?.id) {
      obj2.id = data?.id;
    }
  });
  if (doc?.workflowStepResponses) {
    doc.workflowStepResponses.forEach((item) => {
      if (item.userLabel === "L1") {
        obj2.L1 = { ...item, ...doc?.l1 };
      } else if (item.userLabel === "L2") {
        obj2.L2 = { ...item, ...doc?.l2 };
      } else if (item.userLabel === "L3") {
        obj2.L3 = { ...item, ...doc?.l3 };
      } else if (item.userLabel === "Finance") {
        obj2.Finance = { ...item, ...doc?.l3 };
      }
    });
  }
  if (doc?.id) {
    obj2.file_id = doc?.id;
  }
  obj2.storeCode = doc?.storeCode;
  if (doc?.documentName) {
    obj2.filename = doc?.documentName;
  }
  return obj2;
}

/**
 * Store users: line items live on parent.documents. L1/L2/Finance often get empty parent.documents while
 * children are grouped under pendingApprovedRejectedDocs (and related *Docs arrays).
 */
function collectCashDepositLineItems(parent) {
  const direct = parent?.documents;
  if (Array.isArray(direct) && direct.length > 0) {
    return direct;
  }
  const bucketKeys = [
    "pendingApprovedRejectedDocs",
    "pendingDocs",
    "approvedDocs",
    "rejectedDocs",
    "reworkRequestedDocs",
    "reworkPendingDocs",
  ];
  const seen = new Set();
  const out = [];
  for (const key of bucketKeys) {
    const arr = parent?.[key];
    if (!Array.isArray(arr)) continue;
    for (const doc of arr) {
      const id = doc?.id;
      if (id != null) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      out.push(doc);
    }
  }
  return out;
}

/**
 * Cash deposit list (schema 7 parent): flatten children for the table, same as legacy useApi + double forEach.
 */
export function normalizeCashDepositListResponse(rawResponse) {
  const parents = flattenDocumentsFromGetRecordsResponse(rawResponse);
  const rows = [];
  parents.forEach((parent) => {
    const lineItems = collectCashDepositLineItems(parent);
    if (lineItems.length > 0) {
      lineItems.forEach((child) => rows.push(mapChildDocumentRow(child)));
    } else if (parent?.dataModalAttributeSet?.length) {
      rows.push(mapChildDocumentRow(parent));
    }
  });
  return rows;
}

/**
 * Standard payload for petty cash / cash-deposit approve & reject (advance workflow; no voucher Send All).
 */
export function buildStandaloneDmsApprovePayload({
  approved = [],
  comment = "",
  rejected = [],
  saveDecisionOnly = false,
}) {
  return {
    approved,
    comment,
    rejected,
    saveDecisionOnly,
  };
}
